/*
 * The Actions cell of an AI Recommendation row (owner 10-07: V4 "order ledger", "except that the
 * 'review' column/button seems pointless"; careiq-n3/actions-views). Pure: no React.
 *
 * The Actions column is the visit's order list. What one cell holds:
 *   order   the rec's own order (rec.action): a grey verb over / beside the order's short name in teal,
 *           and the whole cell is ONE button that adds it, its quiet ⊕ at the cell's right edge (every ⊕
 *           in one straight line). Teal means "places an order" and nothing else.
 *   shared  the order another row adds (careiq.n3.4 rec.order_shared_with: the server offers one order
 *           once): its name in grey and a pointer "↑ eGFR" to the row that adds it, never a second ⊕
 *   link    no order, somewhere to go (n3.4 rec.next_step {kind: "link", label, target}): an ink link
 *           with › to the chart tab where the work happens (RPM, the encounters, documents, labs); its
 *           (i) says why there is no order (n3.4 rec.no_order_reason, when the server sends one)
 *   plain   no order and nowhere to go: the action in plain grey; its (i) says why (n3.4
 *           rec.no_order_reason) when the server says so
 *   null    nothing for the Actions column (a risk statement, a fallback sentence)
 * careiq.n3.4 sends `action.display` (the short order name) and `action_verb` (the short verb, from the
 * server's templates, never the model). An n3.3 (or older) body falls back: the name is the action's
 * label without its "Add" (or its catalogue name), the verb is the served headline's action half
 * (recText.js splitHeadline), and there is no next step, shared pointer or reason.
 */
import { actionShort, capAction } from "./recText";

const squash = (s) => (s == null ? "" : String(s).replace(/\s+/g, " ").trim());

// next_step targets -> the chart tab that holds the work (patientchartwrap.jsx renderContent, navframe.jsx)
export const NEXT_TAB = { labs: 1, documents: 2, rpm: 4, encounters: 5 };
export const NEXT_PLACE = { labs: "the Labs tab", documents: "the Documents tab", rpm: "the RPM tab", encounters: "Prior visits" };

// A verb this short sits in the wide row's verb column (the order names then line up like an order
// sheet); a longer one (an n3.3 headline's action half) goes on a line of its own above the name.
export const VERB_COL_MAX = 12;

// The order's short name: n3.4 action.display; else the label without its "Add" ("Add Lipids" ->
// "Lipids"), else the catalogue name. A one-word label that only names the kind of order ("Add referral")
// takes the catalogue name when that is short and says which one ("Referral - Ophthalmology"): two rows
// never offer the same "Referral".
const DISPLAY_MAX = 32;
export const orderDisplay = (action) => {
  if (!action) return "";
  const shown = squash(action.display);
  if (shown) return shown;
  const short = squash(action.label) ? actionShort(action.label) : "";
  const cat = squash(action.ordername);
  if (short && !/\s/.test(short) && cat && cat.length <= DISPLAY_MAX && cat.toLowerCase() !== short.toLowerCase()
    && new RegExp(`(^|[^a-z])${short.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z]|$)`).test(cat.toLowerCase())) return cat;
  return short || cat;
};

const sameWords = (a, b) => squash(a).toLowerCase() === squash(b).toLowerCase();

// The verb over the order name: n3.4 action_verb, else the headline's action half; never a repeat of
// the name itself ("Referral" over "Referral").
export const cellVerb = (rec, hlAction, name = "") => {
  const v = capAction(squash(rec && rec.action_verb) || squash(hlAction));
  return v && !sameWords(v, name) ? v : "";
};

// {kind, verb, name | label | text, ...} or null (see above).
export const actionsCell = (rec, hlAction = "") => {
  if (!rec) return null;
  const act = rec.action || null;
  const shared = squash(rec.order_shared_with);
  if (shared && shared !== rec.issue_key) {
    const name = orderDisplay(act);
    return { kind: "shared", verb: cellVerb(rec, hlAction, name), name, to: shared };
  }
  if (act) {
    const name = orderDisplay(act);
    return { kind: "order", verb: cellVerb(rec, hlAction, name), name };
  }
  const ns = rec.next_step;
  if (ns && ns.kind === "link" && Object.prototype.hasOwnProperty.call(NEXT_TAB, ns.target) && squash(ns.label)) {
    const label = squash(ns.label);
    return { kind: "link", verb: cellVerb(rec, hlAction, label), label, target: ns.target, reason: squash(rec.no_order_reason) };
  }
  const text = capAction(squash(hlAction) || squash(rec.action_verb));
  if (text) return { kind: "plain", text, reason: squash(rec.no_order_reason) };
  return null;
};

// The pointer of a shared order: the row that adds it ({to, label, dir: "↑" | "↓", target}), from the
// rows in display order (lane order). `labelOf(target)` names that row (its lead, else its finding).
export const pointerOf = (rec, ordered, labelOf) => {
  const to = squash(rec && rec.order_shared_with);
  if (!to) return null;
  const list = ordered || [];
  const i = list.findIndex((r) => r.issue_key === rec.issue_key);
  const j = list.findIndex((r) => r.issue_key === to);
  const target = j >= 0 ? list[j] : null;
  if (!target) return { to, label: "", dir: "", target: null };
  return { to, label: squash(labelOf ? labelOf(target) : "") || orderDisplay(target.action), dir: i >= 0 && j > i ? "↓" : "↑", target };
};

// The orders the drawer would place (the header's count): one per order key, a shared row never twice.
export const ledgerCount = (rows, isAdded) => {
  const keys = new Map();
  (rows || []).forEach((r) => {
    if (!r || !r.action || squash(r.order_shared_with)) return;
    const k = r.action.order_key || orderDisplay(r.action);
    if (!keys.has(k)) keys.set(k, Boolean(isAdded && isAdded(r)));
    else if (isAdded && isAdded(r)) keys.set(k, true);
  });
  const all = [...keys.values()];
  return { total: all.length, added: all.filter(Boolean).length };
};

// The other rows one order covers: the ones that point to `item` (order_shared_with) or carry the same
// order key. Adding it handles them too; undoing it reopens them.
export const sharersOf = (list, item, key) => (list || []).filter((r) => r && item && r.issue_key !== item.issue_key && (
  squash(r.order_shared_with) === item.issue_key || Boolean(key && r.action && r.action.order_key === key)));

// The band's one item while recording (adversarial review 10-07): with no review control, adding an order is
// the only thing that handles a row, so the band leads with the first open row (lane order) whose order is
// still to place; else the first open row whose order is not placed yet; else the first open row. A row that
// cannot be handled from the band (a link, a plain action, an order already on the visit, a pointer whose row
// is handled) never holds the band for the whole recording. `ordered`: the open rows in lane order;
// `infoOf(row)`: the container's infoFor; `byKey`: every rec by issue key. Returns `ordered`, its lead first.
export const bandFirst = (ordered, infoOf, byKey) => {
  const list = ordered || [];
  const placedHere = (r) => { const i = r && infoOf ? infoOf(r) : null; return Boolean(i && i.state === "added"); };
  const placed = (r) => {
    const to = squash(r.order_shared_with);
    if (to) {
      const t = byKey && byKey.get ? byKey.get(to) : null;
      return Boolean(t && (placedHere(t) || t.status !== "open"));
    }
    return Boolean(r.action) && placedHere(r);
  };
  const toPlace = (r) => Boolean(r.action) && !squash(r.order_shared_with) && !placedHere(r);
  const top = list.find(toPlace) || list.find((r) => !placed(r)) || list[0];
  return top ? [top, ...list.filter((r) => r !== top)] : [];
};
